// 계획 관리 다이얼로그 - 세금계산서 1회차의 수금(지급) 계획을 선금/중도금/잔금 등 N개로
// 쪼개거나 다시 합친다. 사용자 승인(2026-10-21): 계획명은 선택(선금/중도금/잔금/기타) +
// 자유 메모. 배분(수금)이 걸린 계획은 잠겨서 금액/날짜를 못 바꾸고 지울 수 없다
// (BillCollectPlanService.replacePlans와 같은 규칙 - 여기서도 미리 걸러 사용자 혼란을 줄인다).
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

const fmt = (value) => {
  const n = Number(String(value ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? n.toLocaleString('ko-KR') : '0';
};

const PLAN_NAMES = ['선금', '중도금', '잔금', '기타'];

let tempSeq = 0;
const nextTempId = () => `tmp-${Date.now()}-${tempSeq++}`;

export default function BillPlanEditDialog({ open, onClose, row, onSave, submitting }) {
  const [rows, setRows] = useState([]);
  const invTot = Number(String(row?.invTot || '0').replace(/,/g, ''));

  useEffect(() => {
    if (open) {
      const initial = (row?.plans || []).map((p) => ({
        // planUuid가 없는(아직 저장 전인) 계획 라인이 여러 개면 전부 key가 undefined가 돼
        // 행을 못 구분한다 - InvoiceSplitDialog add-mode(계획 쪼개기, 2026-10-21)가 이 다이얼로그를
        // 로컬 draft(아직 서버에 없는 계획)에도 재사용하면서 필요해졌다.
        key: p.planUuid || nextTempId(),
        planUuid: p.planUuid,
        planNm: p.planNm || '기타',
        planMemo: p.planMemo || '',
        planDt: p.planDt ? dayjs(p.planDt) : dayjs(),
        planAmt: p.planAmt || '0',
        locked: Boolean(p.locked),
        allocatedAmt: p.allocatedAmt,
        status: p.status,
      }));
      setRows(initial.length > 0 ? initial : [{
        key: nextTempId(), planUuid: null, planNm: '전액', planMemo: '', planDt: dayjs(),
        planAmt: String(invTot), locked: false,
      }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, row?.invUuid]);

  const sum = rows.reduce((acc, r) => acc + (Number(r.planAmt) || 0), 0);
  const diff = invTot - sum;
  const balanced = diff === 0;

  const updateRow = (key, field, value) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, [field]: value } : r)));
  };

  const addRow = () => {
    setRows((prev) => [...prev, {
      key: nextTempId(), planUuid: null, planNm: '기타', planMemo: '', planDt: dayjs(),
      planAmt: '0', locked: false,
    }]);
  };

  const removeRow = (key) => {
    setRows((prev) => prev.filter((r) => r.key !== key));
  };

  const handleSave = () => {
    const payload = rows.map((r) => ({
      planUuid: r.planUuid,
      planNm: r.planNm,
      planMemo: r.planMemo,
      planDt: r.planDt ? r.planDt.format('YYYY-MM-DD') : '',
      planAmt: String(Number(r.planAmt) || 0),
    }));
    onSave?.(payload);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        계획 관리 - {row?.invNo || ''}
        <Typography variant="body2" color="text.secondary">
          회차 금액 {fmt(invTot)}원 - 선금/중도금/잔금처럼 N개로 나눌 수 있습니다
        </Typography>
      </DialogTitle>
      <DialogContent>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>계획명</TableCell>
                <TableCell>메모</TableCell>
                <TableCell>계획일</TableCell>
                <TableCell align="right">계획금액</TableCell>
                <TableCell>상태</TableCell>
                <TableCell align="right">삭제</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.key}>
                  <TableCell sx={{ minWidth: 110 }}>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      value={r.planNm}
                      disabled={r.locked}
                      onChange={(e) => updateRow(r.key, 'planNm', e.target.value)}
                    >
                      {PLAN_NAMES.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                    </TextField>
                  </TableCell>
                  <TableCell sx={{ minWidth: 140 }}>
                    <TextField
                      size="small"
                      fullWidth
                      value={r.planMemo}
                      disabled={r.locked}
                      onChange={(e) => updateRow(r.key, 'planMemo', e.target.value)}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 150 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        value={r.planDt}
                        format="YYYY-MM-DD"
                        disabled={r.locked}
                        onChange={(v) => updateRow(r.key, 'planDt', v)}
                        slotProps={{ textField: { size: 'small', variant: 'standard' } }}
                      />
                    </LocalizationProvider>
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 130 }}>
                    <TextField
                      type="number"
                      size="small"
                      variant="standard"
                      disabled={r.locked}
                      value={r.planAmt}
                      onChange={(e) => updateRow(r.key, 'planAmt', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    {r.locked ? (
                      <Tooltip title={`배분액 ${fmt(r.allocatedAmt)}원 - 수금이 걸려 금액/날짜를 바꿀 수 없습니다`}>
                        <Chip size="small" icon={<LockIcon fontSize="small" />} label={r.status || '잠김'} color="warning" />
                      </Tooltip>
                    ) : (
                      <Chip size="small" label={r.status || '예정'} variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" disabled={r.locked || rows.length <= 1} onClick={() => removeRow(r.key)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
          <Button size="small" startIcon={<AddIcon />} onClick={addRow}>계획 추가</Button>
          <Chip
            label={balanced ? '회차 금액과 일치' : `차액 ${fmt(diff)}원`}
            color={balanced ? 'success' : 'error'}
            variant={balanced ? 'outlined' : 'filled'}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>취소</Button>
        <Button variant="contained" disabled={!balanced || submitting} onClick={handleSave}>저장</Button>
      </DialogActions>
    </Dialog>
  );
}
